import { ExchangeRateOrigin } from './exchangeRate';

export type PaymentStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'REVERSED';

/** Phase 97 — how the approval workflow names each status. A PENDING
 * payment is waiting for a finance reviewer and counts towards nothing. */
export const PAYMENT_STATUS_LABEL: Record<PaymentStatus, string> = {
  PENDING: 'Pending finance approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  REVERSED: 'Reversed',
};
export type PaymentMethod = 'CASH' | 'BANK_TRANSFER' | 'CARD' | 'ONLINE_PAYMENT';
export type PaymentType = 'PAYMENT' | 'DISCOUNT' | 'SCHOLARSHIP' | 'REFUND';

type CurrencyRef = { id: number; code: string; symbol: string | null };
type UserRef = { id: number; firstName: string; lastName: string };

export interface Payment {
  id: number;
  /** TEMP-PAY… until approved, then PAY…. */
  paymentNumber: string;
  /** The temporary reference, kept as history once approved. */
  temporaryPaymentNumber?: string | null;
  type: PaymentType;
  amount: string;
  paymentMethod: PaymentMethod | null;
  referenceNumber: string | null;
  paymentDate: string;
  status: PaymentStatus;
  notes: string | null;
  createdAt: string;
  student: { id: number; studentNumber: string; firstName: string; lastName: string };
  application: { id: number; applicationNumber: string } | null;
  category: { id: number; name: string };
  currency: CurrencyRef;
  recordedBy: UserRef;
  approvedBy: UserRef | null;
  receipt: {
    id: number;
    receiptNumber: string;
    issuedAt: string;
    /** Phase 104 — receipts generated with a chosen currency and rate. */
    documents?: ReceiptDocument[];
  } | null;
  approvedAt?: string | null;
  /** Phase 104 — the rate this payment was recorded at. */
  exchangeRate?: string | null;
  lkrAmount?: string | null;
  taxAmount?: string | null;
  usedExchangeRate?: { id: number; origin: ExchangeRateOrigin; sourcePublishedAt: string } | null;
  /** Phase 107 — every uploaded receipt / bank slip (oldest first), and an
   * open request from Finance for another. */
  evidence?: PaymentEvidence[];
  evidenceRequestedAt?: string | null;
  evidenceRequestKind?: PaymentEvidenceRequestKind | null;
  evidenceRequestReason?: string | null;
  evidenceRequestedBy?: UserRef | null;
  /** Phase 97 — the reviewer's reason, and the correct-and-resubmit links. */
  rejectionReason?: string | null;
  /** Reversal (2026-11-02): the latest reversal, receipts voided by
   * reversals, and every review decision in order. */
  reversedBy?: UserRef | null;
  reversedAt?: string | null;
  reversalReason?: string | null;
  voidedReceipts?: Array<{ id: number; receiptNumber: string; issuedAt: string; voidedAt: string | null; voidReason: string | null; voidedBy: UserRef | null }>;
  reviewEvents?: PaymentReviewEvent[];
  resubmittedFrom?: { id: number; paymentNumber: string } | null;
  resubmission?: { id: number; paymentNumber: string; status: PaymentStatus } | null;

  /** Payment slip / receipt EVIDENCE — deliberately independent of
   * `status`: uploading one is optional and never itself a confirmation
   * (see Payment/Document Checklist correction). */
  slipOriginalName: string | null;
  slipUploadedAt: string | null;
  slipUploadedBy: UserRef | null;
}

export interface PaymentReviewEvent {
  id: number;
  action: 'APPROVED' | 'REJECTED' | 'REVERSED';
  at: string;
  reason: string | null;
  receiptNumber: string | null;
  selfApproved: boolean;
  by: UserRef;
}

export type PaymentItemStatus = 'UNPAID' | 'PARTIALLY_PAID' | 'PENDING_CONFIRMATION' | 'PAID';

export interface PaymentChecklistItem {
  categoryId: number;
  name: string;
  description: string | null;
  isRequired: boolean;
  // SSCL tax / exchange-rate payment overhaul (Phase 79)
  isTaxable: boolean;
  isRegistrationRequirement: boolean;
  foundationExamOnly?: boolean;
  requiredAmount: number | null;
  taxAmount: number | null;
  // Payment Checklist "Total with Tax" correction — Student Amount + TAX
  // (equals requiredAmount itself for a non-taxable item). LKR is derived
  // from this value, server-side, never recomputed on the frontend.
  totalWithTax: number | null;
  lkrAmount: number | null;
  exchangeRateUnavailable: boolean;
  currency: CurrencyRef | null;
  paidAmount: number;
  outstandingAmount: number | null;
  status: PaymentItemStatus;
  hasPendingPayment: boolean;
  hasRejectedPayment: boolean;
}

export interface PaymentChecklist {
  items: PaymentChecklistItem[];
  totalRequired: number;
  totalPaid: number;
  totalOutstanding: number;
  // SSCL tax / exchange-rate payment overhaul (Phase 79) — true once the 4
  // registration-requirement items (base + SSCL) are fully paid; other fee
  // items being unpaid never affects this.
  registrationPaymentComplete: boolean;
  /** The Registration Fee's own status (2026-11-06). */
  registrationPaymentStatus?: import('../components/payments/registrationPayment').RegistrationPaymentStatus;
}

export interface LedgerCharge {
  applicationId: number;
  applicationNumber: string;
  university: string;
  course: string;
  tuitionFee: number;
  currency: CurrencyRef | null;
  paid: number;
  outstanding: number;
}

export interface LedgerEntry {
  id: number;
  paymentNumber: string;
  type: PaymentType;
  status: PaymentStatus;
  category: { id: number; name: string };
  amount: number;
  currency: CurrencyRef;
  applicationId: number | null;
  paymentDate: string;
  runningBalance: number;
}

export interface LedgerCurrencySummary {
  currency: CurrencyRef;
  totalCharges: number;
  totalCollected: number;
  totalDiscounts: number;
  totalRefunded: number;
  outstandingBalance: number;
}

export interface StudentLedger {
  charges: LedgerCharge[];
  entries: LedgerEntry[];
  summary: LedgerCurrencySummary[];
}

export interface FinanceDashboardStats {
  todayCollection: Array<{ currency: CurrencyRef; total: number }>;
  monthCollection: Array<{ currency: CurrencyRef; total: number }>;
  collectionByCounselor: Array<{ counselor: UserRef; currency: CurrencyRef; total: number }>;
  byType: Array<{ type: PaymentType; currency: CurrencyRef; total: number }>;
}

/** Generate Receipt (Phase 104). */
export type DocumentCurrency = 'USD' | 'LKR';

export interface ReceiptGenerationPayload {
  currency: DocumentCurrency;
  exchangeRateId?: number;
}

export interface ReceiptPreview {
  receiptNumber: string;
  currency: DocumentCurrency;
  paymentCurrency: string;
  amount: number;
  taxAmount: number | null;
  totalAmount: number;
  amountUsd: number | null;
  amountLkr: number | null;
  receiptAmount: number;
  exchangeRate: number | null;
  exchangeRateDate: string | null;
  exchangeRateOrigin: ExchangeRateOrigin | null;
  exchangeRateId: number | null;
}

/** One generated receipt version — an immutable snapshot. */
export interface ReceiptDocument {
  id: number;
  version: number;
  currency: DocumentCurrency;
  amountUsd: string | null;
  amountLkr: string | null;
  exchangeRate: string | null;
  exchangeRateDate: string | null;
  exchangeRateOrigin: ExchangeRateOrigin | null;
  generatedAt: string;
  generatedBy: { id: number; firstName: string; lastName: string };
}

/** Payment evidence (Phase 107). */
export type PaymentEvidenceRequestKind = 'REPLACEMENT' | 'ADDITIONAL';

export interface PaymentEvidence {
  id: number;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  uploadedAt: string;
  uploadedBy: { id: number; firstName: string; lastName: string } | null;
}
